import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import api from 'services/api'
import { useDesktop } from 'components/core/SplitPanes'
import FilterHeader from './FilterHeader'
import MomentsSplit from './MomentsSplit'

// The moments step 4a produced (docs/audio-streaming/step-4-quotes.md, "Admin:
// seeing moments"). Nothing reaches the app yet — the feed and the push are
// 4b — so this is the only place the selector's and the judge's output can be
// read: every non-staff user's moments, newest first; on production other
// staff users' are excluded, as on the search page. Never a deleted one.
//
// The list and the picked moment side by side (`MomentsSplit`), under the
// page's title and the filters, which stay pinned; one audio element plays
// for all of it. The spans the selector read are the page's other view
// (`SelectionSpans`).
//
// `feed` lists one account's feed instead (`getFeed`): their released moments
// and their friends' that they may read, newest release first, by the
// phone's own rules, so the state, media and search filters have nothing to
// narrow and are not offered.

const PAGE = 30
// The phone plays the quote window plus this much tail.
const QUOTE_TAIL_S = 0.15

// Step 5: a moment waits for its owner's review (pending) until a press of
// "share" keeps it (kept); only a kept one is in their feed. Hidden covers a
// Delete in the review and a hide from the feed alike.
const STATES = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Awaiting review' },
  { value: 'kept', label: 'Kept' },
  { value: 'hidden', label: 'Hidden' },
]

// The media rule, asked whatever its mode: what `withhold` would hide is a
// score at or above the cutoff (the proto's rule) and, only when the router
// flags are enforced, the router's music/TV flag. The checkbox, ticked by
// default, drops those rows (`media=clean`); unchecked, the operator sees what
// the rule keeps from the phone, each card marked, and the router's flag on
// its own as a shadow chip. It composes with the state — "Awaiting review"
// with the box ticked is the phone's tray once the mode is withhold.

// The page's one audio element and what it plays. The Reports page plays a
// reported quote's whole cut through it too (`playWhole`).
export const usePlayer = () => {
  const audioRef = useRef(null)
  const urls = useRef({})
  // { id, stopAt, whole }: `whole` is the whole moment (to the cut's end),
  // otherwise the quote's own window, which stops at `stopAt`.
  const [current, setCurrent] = useState(null)
  const [time, setTime] = useState(0)
  const [paused, setPaused] = useState(true)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(null)
  // `current` as the element's own handlers read it. It changes before the
  // element moves, so a timeupdate or a frame landing before the render can't
  // hold the new position against the last play's stop and pause it.
  const playing = useRef(null)
  // The latest press, so a cut that lands after a later press doesn't play
  // over it.
  const asked = useRef(null)

  useEffect(
    () => () => Object.values(urls.current).forEach(URL.revokeObjectURL),
    []
  )

  // The cut's object URL, fetched once per moment: the bearer header rides on
  // axios, which an <audio src> could not send.
  const fetchCut = useCallback(async (moment) => {
    setLoading(moment.id)
    try {
      const blob = await api.streamMetrics.getMomentAudio({
        momentId: moment.id,
      })
      const url = URL.createObjectURL(blob)
      urls.current[moment.id] = url
      return url
    } catch (err) {
      setError({ id: moment.id, message: err.message })
      return null
    } finally {
      setLoading(null)
    }
  }, [])

  // A cut already fetched plays inside the press. Safari lets an element play
  // only from a tap until it has played once, and a fetch can outlast the
  // tap, so before the first fetch the element is started and stopped while
  // the tap is still on.
  const start = useCallback(
    async (moment, from, stopAt, whole) => {
      const audio = audioRef.current
      if (!audio) return
      setError(null)
      asked.current = moment.id
      let url = urls.current[moment.id]
      if (!url) {
        if (!audio.getAttribute('src')) {
          audio.play().catch(() => {})
          audio.pause()
        }
        url = await fetchCut(moment)
        if (!url || asked.current !== moment.id) return
      }
      playing.current = { id: moment.id, stopAt, whole }
      if (audio.src !== url) audio.src = url
      audio.currentTime = from
      setTime(from)
      setCurrent(playing.current)
      audio.play().catch(() => {})
    },
    [fetchCut]
  )

  // The quote's play button: the quote's own window plus the phone's tail,
  // from its first word every time. A press while the quote plays pauses it.
  const playQuote = useCallback(
    (moment) => {
      const audio = audioRef.current
      if (
        current?.id === moment.id &&
        !current.whole &&
        audio &&
        !audio.paused
      ) {
        audio.pause()
        return
      }
      const quote = moment.quote || {}
      if (quote.start == null) {
        start(moment, 0, null, true)
        return
      }
      start(moment, quote.start, quote.end + QUOTE_TAIL_S, false)
    },
    [current, start]
  )

  // The whole moment's play/pause, beside the transcript: plays the cut to its
  // end, pauses it, and resumes where it paused rather than from the top.
  const playWhole = useCallback(
    (moment) => {
      const audio = audioRef.current
      if (!audio) return
      if (current?.id === moment.id && current.whole) {
        if (!audio.paused) {
          audio.pause()
          return
        }
        if (audio.currentTime < audio.duration - 0.05) {
          audio.play().catch(() => {})
          return
        }
      }
      start(moment, 0, null, true)
    },
    [current, start]
  )

  // A click on a word or a speaker: the whole moment, from there.
  const seek = useCallback(
    (moment, seconds) => start(moment, Math.max(0, seconds), null, true),
    [start]
  )

  // Whether the quote window has run out, pausing it if so.
  const stopped = useCallback(() => {
    const audio = audioRef.current
    const stopAt = playing.current?.stopAt
    if (stopAt == null || audio.currentTime < stopAt) return false
    audio.pause()
    return true
  }, [])

  const onTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setTime(audio.currentTime)
    stopped()
  }, [stopped])

  // `timeupdate` fires about four times a second, too coarse to light words
  // as they are said; while playing, the playhead is read every frame, as the
  // phone's 20 Hz tick does. The quote window's stop is checked here too, so
  // it lands on the frame rather than up to 250 ms late.
  useEffect(() => {
    if (paused) return undefined
    let frame
    const tick = () => {
      const audio = audioRef.current
      if (audio) {
        setTime(audio.currentTime)
        if (stopped()) return
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [paused, stopped])

  return {
    audioRef,
    playQuote,
    playWhole,
    seek,
    current,
    time,
    paused,
    setPaused,
    loading,
    error,
    onTimeUpdate,
  }
}

// The page's one audio element: a second play stops the first.
export const PlayerAudio = ({ player }) => (
  <audio
    ref={player.audioRef}
    onTimeUpdate={player.onTimeUpdate}
    onPlay={() => player.setPaused(false)}
    onPause={() => player.setPaused(true)}
    onEnded={() => player.setPaused(true)}
    style={{ display: 'none' }}
  />
)

export const TraceDialog = ({ momentId, onClose }) => {
  const [trace, setTrace] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!momentId) return
    setTrace(null)
    setError(null)
    api.streamMetrics
      .getMomentTrace({ momentId })
      .then(setTrace)
      .catch((err) => setError(err.message))
  }, [momentId])
  return (
    <Dialog open={Boolean(momentId)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Selection trace</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!trace && !error && <CircularProgress size={20} />}
        {trace && (
          <Box
            component="pre"
            sx={{
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              m: 0,
            }}
          >
            {JSON.stringify(trace, null, 2)}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

const MomentsBrowser = ({ title, account, user, onUser, feed = false }) => {
  const [state, setState] = useState('all')
  const [hideMedia, setHideMedia] = useState(true)
  const [input, setInput] = useState('')
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [nextBefore, setNextBefore] = useState(null)
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [traceId, setTraceId] = useState(null)
  const player = usePlayer()
  const desktop = useDesktop()
  const userId = user?.id || null
  // Each first page read starts a generation; a read answered after a newer
  // one began (a filter changed under it) is dropped, so a page read for the
  // old filters never lands on the new list.
  const generation = useRef(0)
  // The cursor the next page was asked for: the list's end can be reported
  // twice before the read's spinner replaces it.
  const askedBefore = useRef(null)

  useEffect(() => {
    const timer = setTimeout(() => setQ(input.trim()), 350)
    return () => clearTimeout(timer)
  }, [input])

  const load = useCallback(
    async (before) => {
      if (!before) generation.current += 1
      const mine = generation.current
      setLoading(true)
      try {
        const data = feed
          ? await api.streamMetrics.getFeed({ userId, limit: PAGE, before })
          : await api.streamMetrics.getMoments({
              userId,
              state,
              media: hideMedia ? 'clean' : 'any',
              q,
              limit: PAGE,
              before,
            })
        if (mine !== generation.current) return
        setRows((current) =>
          before ? [...current, ...data.moments] : data.moments
        )
        setNextBefore(data.next_before)
        setMeta(data)
        setError(null)
      } catch (err) {
        if (mine !== generation.current) return
        setError(err.message)
        // A first page that failed leaves the old list on view; its cursor
        // must not read on under the new filters.
        if (!before) setNextBefore(null)
      } finally {
        if (mine === generation.current) setLoading(false)
      }
    },
    [feed, userId, state, hideMedia, q]
  )

  useEffect(() => {
    load(null)
  }, [load])

  // The list's end reads the next page (`SplitPanes`' `onEnd`): there is no
  // More button. A page that failed is not asked for again on its own, which
  // would loop on a failing read; the list's foot offers it instead.
  const loadMore = useCallback(() => {
    if (!nextBefore || askedBefore.current === nextBefore) return
    askedBefore.current = nextBefore
    load(nextBefore)
  }, [load, nextBefore])
  const retryMore = () => {
    askedBefore.current = null
    loadMore()
  }
  const active = [
    user,
    !feed && state !== 'all',
    !feed && hideMedia,
    !feed && q,
  ].filter(Boolean).length

  const summary = useMemo(() => {
    if (feed) {
      const theirs = rows.filter((m) => m.user_id === userId).length
      return `${rows.length}${
        nextBefore ? '+' : ''
      } moments in their feed · ${theirs} theirs · ${
        rows.length - theirs
      } their friends’`
    }
    const flagged = rows.filter((m) => m.media_would_withhold).length
    const routerOnly = rows.filter(
      (m) => !m.media_would_withhold && m.media_router_only
    ).length
    const hidden = rows.filter((m) => m.disabled_at_ms).length
    const pending = rows.filter(
      (m) => !m.disabled_at_ms && m.released_at_ms == null
    ).length
    const kept = rows.filter(
      (m) => !m.disabled_at_ms && m.released_at_ms != null
    ).length
    return `${rows.length}${
      nextBefore ? '+' : ''
    } moments · ${pending} awaiting review · ${kept} kept · ${hidden} hidden · ${flagged} media-flagged · ${routerOnly} router-flagged (shadow)`
  }, [feed, userId, rows, nextBefore])

  // On a phone these fold behind the Filters button (`FilterHeader`).
  const filters = !feed && (
    <>
      <ToggleButtonGroup
        value={state}
        exclusive
        size="small"
        onChange={(event, next) => next && setState(next)}
      >
        {STATES.map(({ value, label }) => (
          <ToggleButton key={value} value={value}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={hideMedia}
            onChange={(event) => setHideMedia(event.target.checked)}
          />
        }
        label="Hide media-flagged"
        sx={{ mr: 0 }}
      />
      <TextField
        size="small"
        placeholder="Search quotes and hooks"
        value={input}
        onChange={(event) => setInput(event.target.value)}
        sx={{ width: 280, maxWidth: '100%' }}
      />
    </>
  )

  const header = (
    <>
      <FilterHeader
        title={title}
        account={account}
        filters={filters}
        active={active}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
        }}
      >
        {desktop && filters}
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {summary}
          {feed
            ? meta?.staff_excluded
              ? ' · other staff’s moments left off (production)'
              : ''
            : meta?.staff_excluded
            ? ' · other staff excluded (production)'
            : ' · every account, staff included (develop/staging)'}
          {meta?.media
            ? ` · media rule ${meta.media.mode}, cutoff ${
                meta.media.cutoff
              }, router flags ${
                meta.media.router_flags ? 'enforced' : 'shadow'
              }`
            : ''}
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
    </>
  )

  return (
    <>
      <MomentsSplit
        header={header}
        empty={
          !loading &&
          !error &&
          rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
              {feed
                ? 'Nothing in their feed yet: no moment of theirs released, and none of their friends’ they can read.'
                : `No moments${
                    user ? ' for this account' : ''
                  } yet. A span with none is explained by its ledger, under Spans.`}
            </Typography>
          )
        }
        rows={rows}
        player={player}
        onTrace={setTraceId}
        onUser={onUser}
        mediaMode={meta?.media?.mode}
        onEnd={nextBefore && !loading ? loadMore : null}
        footer={
          rows.length > 0 &&
          (loading || (error && nextBefore)) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <Button onClick={retryMore}>Try again</Button>
              )}
            </Box>
          )
        }
      />
      <PlayerAudio player={player} />
      <TraceDialog momentId={traceId} onClose={() => setTraceId(null)} />
    </>
  )
}

export default MomentsBrowser
